import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { useSmartBack } from '../utils/useSmartBack.js';
import { localizeMessage } from '../utils/toast.js';
import { MARKET } from '../utils/market.js';
import './OrgProgramSubmit.css';

const FB_SECTORS = [
  { id: 'ecology', label: 'Ecology', color: '#16a34a', icon: 'fas fa-leaf' },
  { id: 'international', label: 'International', color: '#2563eb', icon: 'fas fa-globe-americas' },
  { id: 'technology', label: 'Technology', color: '#d97706', icon: 'fas fa-microchip' },
  { id: 'medical', label: 'Medical', color: '#dc2626', icon: 'fas fa-heartbeat' },
  { id: 'education', label: 'Education', color: '#7c3aed', icon: 'fas fa-graduation-cap' },
  { id: 'business', label: 'Business', color: '#1d4ed8', icon: 'fas fa-briefcase' },
  { id: 'arts', label: 'Arts & Culture', color: '#be185d', icon: 'fas fa-palette' },
  { id: 'science', label: 'Science', color: '#0891b2', icon: 'fas fa-flask' },
  { id: 'sports', label: 'Sports', color: '#ea580c', icon: 'fas fa-running' },
  { id: 'agriculture', label: 'Agriculture', color: '#65a30d', icon: 'fas fa-tractor' },
  { id: 'local', label: 'Local', color: '#8b5cf6', icon: 'fas fa-map-marker-alt' },
  { id: 'media', label: 'Media', color: '#db2777', icon: 'fas fa-photo-video' },
];

const FB_CATEGORIES = [
  { id: 'education', label: 'Education', color: '#7c3aed', icon: 'fas fa-graduation-cap' },
  { id: 'scholarship', label: 'Scholarship', color: '#2563eb', icon: 'fas fa-money-bill-wave' },
  { id: 'fellowship', label: 'Fellowship', color: '#d97706', icon: 'fas fa-handshake' },
  { id: 'erasmusplus', label: 'Erasmus Plus', color: '#2563eb', icon: 'fas fa-plane' },
  { id: 'training', label: 'Training', color: '#d97706', icon: 'fas fa-book' },
  { id: 'grant', label: 'Grant', color: '#dc2626', icon: 'fas fa-dollar-sign' },
  { id: 'internship', label: 'Internship', color: '#7c3aed', icon: 'fas fa-user-tie' },
  { id: 'conference', label: 'Conference', color: '#be185d', icon: 'fas fa-comments' },
  { id: 'online', label: 'Online', color: '#0891b2', icon: 'fas fa-laptop' },
  { id: 'volunteering', label: 'Volunteering', color: '#16a34a', icon: 'fas fa-hands-helping' },
  { id: 'event', label: 'Event', color: '#be185d', icon: 'fas fa-calendar-alt' },
  { id: 'award', label: 'Award', color: '#f59e0b', icon: 'fas fa-trophy' },
];

function loadUiSettings() {
  try {
    const raw = localStorage.getItem('admin_program_settings');
    if (!raw) return { sectors: FB_SECTORS, categories: FB_CATEGORIES };
    const parsed = JSON.parse(raw);
    const sectors = parsed.sectors?.length ? parsed.sectors : FB_SECTORS;
    const categories = (parsed.categories || []).filter(c => c.id !== 'all');
    return {
      sectors,
      categories: categories.length ? categories : FB_CATEGORIES,
    };
  } catch {
    return { sectors: FB_SECTORS, categories: FB_CATEGORIES };
  }
}

function ChipToggle({ items, selected, onToggle, accentKey = 'color' }) {
  return (
    <div className="ops-chip-grid">
      {items.map((item) => {
        const active = selected.includes(item.id);
        const accent = item[accentKey] || '#32614d';
        return (
          <button
            key={item.id}
            type="button"
            className={`ops-chip${active ? ' ops-chip-active' : ''}`}
            style={{
              borderColor: active ? accent : 'rgba(50,97,77,0.2)',
              background: active ? accent : '#fff',
              color: active ? '#fff' : '#3d5c47',
            }}
            onClick={() => {
              if (active) onToggle(selected.filter((x) => x !== item.id));
              else onToggle([...selected, item.id]);
            }}
          >
            {active ? '✓ ' : ''}{item.label}
          </button>
        );
      })}
    </div>
  );
}

function OrgProgramSubmit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack('/feed');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [ui, setUi] = useState(() => loadUiSettings());
  useEffect(() => {
    setUi(loadUiSettings());
  }, []);

  const trSectors = ui.sectors.map(s => ({ ...s, label: t(`orgProgramSubmit.sectorLabels.${s.id}`, s.label) }));
  const trCategories = ui.categories.map(c => ({ ...c, label: t(`orgProgramSubmit.categoryLabels.${c.id}`, c.label) }));

  const orgName = user.organizationName || user.username || t('orgProgramSubmit.defaultOrgName');
  // Greencoin rewards are admin-only while the org self-service flow is being
  // trialed (see GREENCOIN_ORG_SELF_SERVICE on the backend) — regular
  // organizers don't see this section at all yet.
  const isAdmin = user?.role === 'admin' || user?.userType === 'admin';

  const [form, setForm] = useState({
    title: '',
    description: '',
    categories: [],
    sectors: [],
    url: '',
    location: '',
    deadline: '',
    end_date: '',
    start_date: '',
    funding_type: '',
    target_group: '',
    format: '',
    education_level: '',
    requirements: '',
  });
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [appFormSaveWarning, setAppFormSaveWarning] = useState('');

  const [applicationFormEnabled, setApplicationFormEnabled] = useState(false);
  const [questions, setQuestions] = useState([]);

  const [greencoinReward, setGreencoinReward] = useState(0);
  const [showGreencoinPanel, setShowGreencoinPanel] = useState(false);
  const [orgGreencoinBalance, setOrgGreencoinBalance] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !isAdmin) return;
    fetch(`${GREENWICH_BACKEND_URL}/api/organization/greencoins`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => { if (data.success) setOrgGreencoinBalance(data.balance); })
      .catch(() => {});
  }, []);

  const MAX_QUESTIONS = 20;
  const QUESTION_MAX_LEN = 150;

  // Practical starter set covering the logistics info every applicant typically needs to
  // confirm (location/date already live on the program itself, but applicants should
  // explicitly acknowledge them) plus the standard "who are you / why" questions. The
  // organizer can freely edit, remove, or add to these — they're just a faster starting
  // point than one blank row.
  const DEFAULT_QUESTIONS = [
    { text: 'Full name and phone number', required: true },
    { text: 'Can you confirm you are available at the program location and dates?', required: true },
    { text: 'Why do you want to join this program?', required: true },
    { text: 'Any additional information (optional)', required: false },
  ];

  const toggleApplicationForm = (checked) => {
    setApplicationFormEnabled(checked);
    if (checked && questions.length === 0) {
      setQuestions(DEFAULT_QUESTIONS.map((q) => ({ ...q })));
    }
  };

  const addQuestion = () => {
    setQuestions((p) => (p.length >= MAX_QUESTIONS ? p : [...p, { text: '', required: true }]));
  };

  const updateQuestion = (idx, patch) => {
    setQuestions((p) => p.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (idx) => {
    setQuestions((p) => p.filter((_, i) => i !== idx));
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleImage = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setCoverImage(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setAppFormSaveWarning('');
    if (!form.title.trim() || !form.description.trim()) {
      setError(t('orgProgramSubmit.errorTitleDesc'));
      setLoading(false);
      return;
    }
    if (!form.categories.length) {
      setError(t('orgProgramSubmit.errorCategory'));
      setLoading(false);
      return;
    }
    if (!form.sectors.length) {
      setError(t('orgProgramSubmit.errorSector'));
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();

      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('requirements', form.requirements || '');
      fd.append('deadline', form.deadline || '');
      fd.append('location', form.location || '');
      fd.append('website', form.url || '');
      fd.append('url', form.url || '');
      fd.append('link', form.url || '');
      fd.append('start_date', form.start_date || '');
      fd.append('end_date', form.end_date || '');
      fd.append('funding_type', form.funding_type || '');
      fd.append('target_group', form.target_group || '');
      fd.append('format', form.format || '');
      fd.append('education_level', form.education_level || '');
      fd.append('source', orgName);
      fd.append('categoriesJson', JSON.stringify(form.categories));
      fd.append('sectorsJson', JSON.stringify(form.sectors));
      fd.append('greencoinReward', String(greencoinReward || 0));

      fd.append('status', 'pending');
      fd.append('approval_status', 'pending');
      fd.append('submittedBy', user._id || user.id || '');
      fd.append('submittedByType', 'organization');
      fd.append('submittedByEmail', user.email || '');
      fd.append('organizationName', orgName);
      fd.append('organizationEmail', user.email || '');
      fd.append('market', MARKET);

      if (coverImage) fd.append('coverImage', coverImage);

      const res = await fetch(`${GREENWICH_BACKEND_URL}/api/programs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const newProgramId = data.program?._id || data._id || data.id;

        if (applicationFormEnabled && questions.some((q) => q.text.trim())) {
          try {
            const formRes = await fetch(`${GREENWICH_BACKEND_URL}/api/programs/${newProgramId}/application-form`, {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                enabled: true,
                questions: questions.filter((q) => q.text.trim()),
              }),
            });
            if (!formRes.ok) {
              setAppFormSaveWarning('Your program was submitted, but the application form could not be saved. You can add it later from "My Submissions" once approved.');
            }
          } catch {
            setAppFormSaveWarning('Your program was submitted, but the application form could not be saved (network error). You can add it later from "My Submissions" once approved.');
          }
        }

        try {
          const id = data._id || data.id || data.program?._id || `local-${Date.now()}`;
          const newProg = {
            ...(data.program || data),
            _id: id,
            title: form.title,
            description: form.description,
            url: form.url || '',
            categories: form.categories,
            sectors: form.sectors,
            location: form.location || '',
            deadline: form.deadline || '',
            status: 'pending',
            approval_status: 'pending',
            submittedBy: { _id: user._id || user.id, email: user.email },
            submittedByType: 'organization',
            organizationEmail: user.email || '',
            organizationName: orgName,
            createdAt: new Date().toISOString(),
            image_url: coverPreview || '',
          };
          const existing = JSON.parse(localStorage.getItem('org_programs') || '[]');
          localStorage.setItem('org_programs', JSON.stringify([newProg, ...existing]));
        } catch {}

        setSubmitted(true);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.message || d.error || t('orgProgramSubmit.errorSubmitFailed'));
      }
    } catch {
      setError(t('orgProgramSubmit.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm({
      title: '',
      description: '',
      categories: [],
      sectors: [],
      url: '',
      location: '',
      deadline: '',
      end_date: '',
      start_date: '',
      funding_type: '',
      target_group: '',
      format: '',
      education_level: '',
      requirements: '',
    });
    setCoverPreview(null);
    setCoverImage(null);
    setApplicationFormEnabled(false);
    setQuestions([]);
    setAppFormSaveWarning('');
    setGreencoinReward(0);
    setShowGreencoinPanel(false);
  };

  const canSubmit =
    form.title.trim() &&
    form.description.trim() &&
    form.categories.length > 0 &&
    form.sectors.length > 0;

  if (submitted) {
    return (
      <div className="ops-page">
        <div className="ops-success-wrap">
          <div className="ops-success-icon">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <circle cx="28" cy="28" r="28" fill="#e8f5ee" />
              <path d="M18 28l8 8 14-16" stroke="#32614d" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2>{t('orgProgramSubmit.successTitle')}</h2>
          <p>
            {t('orgProgramSubmit.successBodyPre')}<strong>{t('orgProgramSubmit.successBodyBold')}</strong>{t('orgProgramSubmit.successBodyPost')}
          </p>
          {appFormSaveWarning && (
            <div className="ops-error" style={{ marginBottom: 16 }}>{appFormSaveWarning}</div>
          )}
          <div className="ops-success-actions">
            <button className="ops-btn-primary" onClick={() => navigate('/programs/my-submissions')}>
              {t('orgProgramSubmit.viewMySubmissions')}
            </button>
            <button className="ops-btn-ghost" onClick={resetForm}>
              {t('orgProgramSubmit.submitAnother')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-page">
      <div className="ops-header">
        <button className="ops-back" onClick={goBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M5 12l7 7M5 12l7-7" />
          </svg>
        </button>
        <div>
          <h1 className="ops-title">{t('orgProgramSubmit.title')}</h1>
          <p className="ops-subtitle">{t('orgProgramSubmit.subtitle')}</p>
        </div>
        <div className="ops-org-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          </svg>
          {orgName}
        </div>
      </div>

      <div className="ops-card ops-card-wide">
        <div className="ops-form-section">
          <div className="ops-cover-upload" onClick={() => document.getElementById('ops-cover-input').click()}>
            {coverPreview ? (
              <img src={coverPreview} alt={t('orgProgramSubmit.coverImageAlt')} className="ops-cover-preview" />
            ) : (
              <div className="ops-cover-placeholder">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>{t('orgProgramSubmit.uploadCoverImage')}</span>
                <small>{t('orgProgramSubmit.coverImageHint')}</small>
              </div>
            )}
            <input id="ops-cover-input" type="file" accept="image/*" hidden onChange={handleImage} />
          </div>

          <div className="ops-field">
            <label>
              {t('orgProgramSubmit.programTitle')} <span className="ops-req">*</span>
            </label>
            <input
              className="ops-input"
              placeholder={t('orgProgramSubmit.programTitlePlaceholder')}
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>

          <div className="ops-field">
            <label>
              {t('orgProgramSubmit.description')} <span className="ops-req">*</span>
            </label>
            <textarea
              className="ops-input ops-textarea"
              rows={5}
              placeholder={t('orgProgramSubmit.descriptionPlaceholder')}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>

          <div className="ops-field">
            <label>
              {t('orgProgramSubmit.categories')} <span className="ops-req">*</span>
              <span className="ops-hint">{t('orgProgramSubmit.categoriesHint')}</span>
            </label>
            <ChipToggle items={trCategories} selected={form.categories} onToggle={(v) => set('categories', v)} />
          </div>

          <div className="ops-field">
            <label>
              {t('orgProgramSubmit.sectors')} <span className="ops-req">*</span>
              <span className="ops-hint">{t('orgProgramSubmit.sectorsHint')}</span>
            </label>
            <ChipToggle items={trSectors} selected={form.sectors} onToggle={(v) => set('sectors', v)} />
          </div>

          <div className="ops-row">
            <div className="ops-field">
              <label>{t('orgProgramSubmit.programUrl')}</label>
              <input className="ops-input" type="url" placeholder="https://…" value={form.url} onChange={(e) => set('url', e.target.value)} />
            </div>
            <div className="ops-field">
              <label>{t('orgProgramSubmit.source')}</label>
              <input className="ops-input" value={orgName} readOnly style={{ opacity: 0.85 }} />
            </div>
          </div>

          <div className="ops-row">
            <div className="ops-field">
              <label>{t('orgProgramSubmit.location')}</label>
              <input className="ops-input" placeholder={t('orgProgramSubmit.locationPlaceholder')} value={form.location} onChange={(e) => set('location', e.target.value)} />
            </div>
            <div className="ops-field">
              <label>{t('orgProgramSubmit.format')}</label>
              <input className="ops-input" placeholder={t('orgProgramSubmit.formatPlaceholder')} value={form.format} onChange={(e) => set('format', e.target.value)} />
            </div>
          </div>

          <div className="ops-field">
            <label>{t('orgProgramSubmit.educationLevel')}</label>
            <select className="ops-input" value={form.education_level || ''} onChange={(e) => set('education_level', e.target.value)}>
              <option value="">{t('orgProgramSubmit.eduAny')}</option>
              <option value="high_school">{t('orgProgramSubmit.eduHighSchool')}</option>
              <option value="student">{t('orgProgramSubmit.eduStudent')}</option>
              <option value="undergraduate">{t('orgProgramSubmit.eduUndergrad')}</option>
              <option value="graduate">{t('orgProgramSubmit.eduGrad')}</option>
              <option value="doctoral">{t('orgProgramSubmit.eduDoctoral')}</option>
              <option value="professional">{t('orgProgramSubmit.eduProfessional')}</option>
            </select>
          </div>

          <div className="ops-row">
            <div className="ops-field">
              <label>{t('orgProgramSubmit.startDate')}</label>
              <input className="ops-input" type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
            </div>
            <div className="ops-field">
              <label>{t('orgProgramSubmit.endDate')}</label>
              <input className="ops-input" type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
            </div>
          </div>

          <div className="ops-row">
            <div className="ops-field">
              <label>{t('orgProgramSubmit.deadline')}</label>
              <input className="ops-input" type="date" value={form.deadline} onChange={(e) => set('deadline', e.target.value)} />
            </div>
            <div className="ops-field">
              <label>{t('orgProgramSubmit.fundingType')}</label>
              <input className="ops-input" placeholder={t('orgProgramSubmit.fundingTypePlaceholder')} value={form.funding_type} onChange={(e) => set('funding_type', e.target.value)} />
            </div>
          </div>

          <div className="ops-field">
            <label>{t('orgProgramSubmit.targetGroup')}</label>
            <input className="ops-input" placeholder={t('orgProgramSubmit.targetGroupPlaceholder')} value={form.target_group} onChange={(e) => set('target_group', e.target.value)} />
          </div>

          <div className="ops-field">
            <label>{t('orgProgramSubmit.requirements')}</label>
            <textarea
              className="ops-input ops-textarea"
              rows={3}
              placeholder={t('orgProgramSubmit.requirementsPlaceholder')}
              value={form.requirements}
              onChange={(e) => set('requirements', e.target.value)}
            />
          </div>

          {isAdmin && (
          <div className="ops-field ops-greencoin-section">
            <button type="button" className="ops-greencoin-toggle" onClick={() => setShowGreencoinPanel((v) => !v)}>
              🪙 {t('orgProgramSubmit.greencoinToggle')}
              {greencoinReward > 0 && <span className="ops-greencoin-badge">+{greencoinReward}</span>}
            </button>

            {showGreencoinPanel && (
              <div className="ops-greencoin-panel">
                <p className="ops-greencoin-pitch">{t('orgProgramSubmit.greencoinPitch')}</p>
                <div className="ops-row" style={{ alignItems: 'flex-end' }}>
                  <div className="ops-field" style={{ flex: 1 }}>
                    <label>{t('orgProgramSubmit.greencoinAmountLabel')}</label>
                    <input
                      className="ops-input"
                      type="number"
                      min="0"
                      step="1"
                      value={greencoinReward}
                      onChange={(e) => setGreencoinReward(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                    />
                  </div>
                  {orgGreencoinBalance != null && (
                    <div className="ops-greencoin-balance">
                      {t('orgProgramSubmit.greencoinBalance', { balance: orgGreencoinBalance })}
                    </div>
                  )}
                </div>
                {greencoinReward > 0 && orgGreencoinBalance != null && greencoinReward > orgGreencoinBalance && (
                  <p className="ops-greencoin-warning">{t('orgProgramSubmit.greencoinLowBalance')}</p>
                )}
              </div>
            )}
          </div>
          )}

          <div className="ops-field ops-appform-section">
            <label className="ops-appform-toggle-label">
              <input
                type="checkbox"
                className="ops-appform-checkbox"
                checked={applicationFormEnabled}
                onChange={(e) => toggleApplicationForm(e.target.checked)}
              />
              In-platform application form (optional)
            </label>
            <span className="ops-hint" style={{ display: 'block', marginTop: 4 }}>
              Let users apply directly on the platform
            </span>

            {applicationFormEnabled && (
              <div className="ops-appform-box">
                {questions.map((q, idx) => (
                  <div className="ops-appform-row" key={idx}>
                    <div className="ops-appform-question-input-wrap">
                      <input
                        className="ops-input ops-appform-question-input"
                        placeholder={`Question ${idx + 1}`}
                        value={q.text}
                        maxLength={QUESTION_MAX_LEN}
                        onChange={(e) => updateQuestion(idx, { text: e.target.value.slice(0, QUESTION_MAX_LEN) })}
                      />
                      <span className="ops-appform-char-count">{q.text.length}/{QUESTION_MAX_LEN}</span>
                    </div>
                    <label className="ops-appform-required-label">
                      <input
                        type="checkbox"
                        checked={q.required}
                        onChange={(e) => updateQuestion(idx, { required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      className="ops-appform-remove-btn"
                      onClick={() => removeQuestion(idx)}
                      aria-label="Remove question"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {questions.length < MAX_QUESTIONS && (
                  <button type="button" className="ops-appform-add-btn" onClick={addQuestion}>
                    + Add question
                  </button>
                )}

                <p className="ops-appform-note">
                  Applicants will always be able to attach a file (up to 10MB) when applying.
                </p>
              </div>
            )}
          </div>

          {error && <div className="ops-error">{localizeMessage(error)}</div>}

          <div className="ops-review-notice">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {t('orgProgramSubmit.reviewNotice')}
          </div>

          <div className="ops-form-actions">
            <button className="ops-btn-submit" disabled={loading || !canSubmit} onClick={handleSubmit}>
              {loading ? (
                <>
                  <div className="ops-spin" /> {t('orgProgramSubmit.submitting')}
                </>
              ) : (
                <>{t('orgProgramSubmit.submitForReview')}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrgProgramSubmit;
